<template>
    <div class="gmail-layout">
        <!-- Three-column layout -->
        <div class="gmail-columns">
            <!-- Left column: Companies list -->
            <div class="gmail-column companies-column">
                <company-list
                    :title="$t('messages.lead_authors')"
                    :search-label="$t('messages.search_lead_authors')"
                    :companies="ticketAuthors"
                    :is-loading="isLoadingAuthors"
                    :error="authorsError"
                    :selected-company-id="selectedAuthor?.id"
                    @company-selected="selectAuthor"
                />
            </div>

            <!-- Middle column: Tickets list -->
            <div class="gmail-column tickets-column">
                <ticket-list
                    :tickets="tickets"
                    :is-loading="isLoadingTickets"
                    :error="ticketsError"
                    :selected-company-id="selectedAuthor?.id"
                    :selected-ticket-id="selectedTicket?.id"
                    :show-create-button="false"
                    :loading-progress="loadingProgress"
                    :should-highlight-ticket="shouldHighlightTicket"
                    @ticket-selected="selectTicket"
                    @refresh-tickets="refreshTickets"
                />
            </div>

            <!-- Right column: Ticket Chat -->
            <div class="gmail-column chat-column">
                <ticket-chat
                    ref="ticketChat"
                    :ticket="selectedTicket"
                    @ticket-updated="onTicketUpdated"
                    @message-sent="onMessageSent"
                />
            </div>
        </div>

        <!-- No New Ticket Modal for Customer Messages -->

        <!-- Snackbar for notifications -->
        <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
            {{ snackbarText }}
        </v-snackbar>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import {authStore} from '@/composables/store-accessor'
    import CompanyList from './CompanyList.vue'
    import TicketList from './TicketList.vue'
    import TicketChat from './TicketChat.vue'
    import {format} from 'date-fns'
    import {maskSupportName, maskSupportNameInText} from '../../utils/messaging-utils'

    interface Company {
        id: string
        name: string
        company_id?: string
    }

    interface LastMessage {
        senderId: string
        senderName: string
        isTO1Role: boolean
        timestamp: number
        senderExternalId?: string
    }

    interface Ticket {
        id: string
        subject: string
        status: string
        createdAt: number
        updatedAt: number
        companyId: string
        lastMessage?: LastMessage
        cachedMessages?: Message[]
    }

    interface Message {
        id: string
        text: string
        sender: string
        timestamp: number
        isFromUser: boolean
        senderId: string
        senderExternalId?: string
        isTicketAuthor?: boolean
    }

    @Component({
        components: {
            CompanyList,
            TicketList,
            TicketChat,
        },
    })
    export default class CustomerMessaging extends Vue {
        // Ticket type IDs passed from parent
        @Prop({default: () => []}) customerTicketTypeIds!: string[]

        // Ticket authors data
        ticketAuthors: Company[] = []
        selectedAuthor: Company | null = null
        isLoadingAuthors: boolean = false
        authorsError: string | null = null

        // Tickets data
        tickets: Ticket[] = []
        selectedTicket: Ticket | null = null
        isLoadingTickets: boolean = false
        loadingProgress: string = ''
        ticketsError: string | null = null

        // Notification data
        showSnackbar: boolean = false
        snackbarText: string = ''
        snackbarColor: string = 'success'

        // User role cache for external IDs
        userRoleCache: Map<string, string> = new Map()

        // Computed property to check if the current user has TO1 role
        get isTO1User(): boolean {
            return authStore.isTO1
        }

        // Utility function to check if a role is TO1
        isUserTO1Role(role?: string): boolean {
            if (!role) return false
            return (
                role.includes('to1') ||
                role.includes('TO1') ||
                role === 'director' ||
                role === 'supervisor' ||
                role === 'support'
            )
        }

        // Utility function to determine if a ticket should be highlighted
        shouldHighlightTicket(ticket: Ticket): boolean {
            if (!ticket.lastMessage) return false

            // Special case for customer messaging: always show as "not answered" when there's only
            // author's message and message about adding TO1 user (i.e. 2 messages)
            if (this.isTO1User && ticket.cachedMessages && ticket.cachedMessages.length === 2) {
                const messages = ticket.cachedMessages
                const hasAuthorMessage = messages.some(msg => msg.isTicketAuthor)

                if (hasAuthorMessage) {
                    // Check if the other message is from a TO1 user
                    const nonAuthorMessages = messages.filter(msg => !msg.isTicketAuthor)
                    if (nonAuthorMessages.length === 1) {
                        const otherMessage = nonAuthorMessages[0]
                        // Check if this message is from a TO1 user by checking the sender role
                        const senderRole = this.getUserRoleFromHeuristics(otherMessage.senderId, otherMessage.sender)
                        const isTO1Message = this.isUserTO1Role(senderRole)
                        if (isTO1Message) {
                            return true
                        }
                    }
                }
            }

            // For customer messaging, TO1 users should see highlighted tickets
            // when last message is NOT from TO1 role
            if (this.isTO1User) {
                const shouldHighlight = !ticket.lastMessage.isTO1Role
                return shouldHighlight
            }

            // For supplier users (if any access customer messaging): highlight if last message is NOT from the supplier themselves
            const isSupplierUser = authStore.person?.role === 'supplier' || authStore.person?.role?.includes('supplier')
            if (isSupplierUser) {
                const isFromCurrentUser =
                    ticket.lastMessage.senderId === authStore.person?.personId ||
                    ticket.lastMessage.senderExternalId === authStore.person?.personId?.toString()
                return !isFromCurrentUser
            }

            return false
        }

        mounted() {

            // Ensure we have the Zero Star Hotels company ID
            this.ensureZeroStarHotelsCompanyId()

            // Load ticket authors
            this.loadTicketAuthors()
        }

        /**
         * Ensures that we have the internal Intercom company ID for Zero Star Hotels
         * Tries to get it from localStorage first, then falls back to API call if needed
         */
        async ensureZeroStarHotelsCompanyId() {
            try {
                // Check if we already have the ID in localStorage
                let storedId = localStorage.getItem('zeroStarHotelsCompanyId')
                if (storedId) {
                    return storedId
                }

                // Try MongoDB fallback
                try {
                    const tokenForFallback = useCookie('token').value
                    if (tokenForFallback) {
                        const fallbackResponse = await fetch('/proxy/messaging/config?key=zeroStarHotelsCompanyId', {
                            headers: {Accept: 'application/json', Token: tokenForFallback},
                        })
                        if (fallbackResponse.ok) {
                            const fallbackData = await fallbackResponse.json()
                            if (fallbackData.value) {
                                storedId = fallbackData.value
                                localStorage.setItem('zeroStarHotelsCompanyId', storedId!)
                                return storedId
                            }
                        }
                    }
                } catch { /* ignore MongoDB fallback error */ }


                const token = useCookie('token').value
                if (!token) {
                    throw new Error('Authentication token not found')
                }

                // Fetch the company ID from the API
                const response = await fetch('/proxy/intercom/companies?company_id=zero_star_hotels', {
                    headers: {
                        Accept: 'application/json',
                        Token: token,
                    },
                })

                if (!response.ok) {
                    throw new Error(`Failed to fetch company: ${response.status} - ${response.statusText}`)
                }

                const data = await response.json()

                if (data && data.id) {
                    const companyId = data.id

                    // Store it in localStorage for future use
                    localStorage.setItem('zeroStarHotelsCompanyId', companyId)
                    // Persist to MongoDB (fire-and-forget)
                    const token2 = useCookie('token').value
                    if (token2) {
                        fetch('/proxy/messaging/config', {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json', Token: token2},
                            body: JSON.stringify({key: 'zeroStarHotelsCompanyId', value: companyId}),
                        }).catch(() => {})
                    }

                    return companyId
                } else {
                    console.error('Company ID not found in API response:', data)
                    throw new Error('Failed to retrieve Zero Star Hotels company ID')
                }
            } catch (error) {
                console.error('Error ensuring Zero Star Hotels company ID:', error)
                // Don't throw the error, just log it and continue
                return null
            }
        }

        async loadTicketAuthors() {
            this.isLoadingAuthors = true
            this.authorsError = null

            try {
                const token = useCookie('token').value

                if (!token) {
                    throw new Error('Authentication token not found. Please log in.')
                }

                // Check if the user has TO1 role
                const role = authStore.person?.role || ''
                const isTO1User =
                    role.includes('to1') ||
                    role.includes('TO1') ||
                    role === 'director' ||
                    role === 'supervisor' ||
                    role === 'support'

                if (!isTO1User) {
                    throw new Error('Access denied: Only TO1 role users can view customer messages')
                }

                // Use the Express proxy endpoint to get ticket authors for Zero Star Hotels company
                // This will only include contacts with the "lead" role to show customer messages
                // from lead contacts only
                const response = await fetch('/proxy/intercom/ticket-authors?company_id=zero_star_hotels', {
                    headers: {
                        Accept: 'application/json',
                        Token: token,
                    },
                })

                if (!response.ok) {
                    throw new Error(`Error: ${response.status} - ${response.statusText}`)
                }

                const data = await response.json()

                if (data && Array.isArray(data.authors)) {
                    this.ticketAuthors = data.authors.map((author: any) => ({
                        id: author.id,
                        name: author.name || author.email || 'Unknown Author',
                        company_id: author.id, // Using the author ID as company_id for compatibility
                    }))


                    if (this.ticketAuthors.length === 0) {
                        this.authorsError =
                            'No lead authors found for Zero Star Hotels. Make sure the company is initialized and has contacts with the "lead" role.'
                    }
                } else {
                    console.error('Invalid response format:', data)
                    throw new Error('Invalid response format from server')
                }
            } catch (error: any) {
                console.error('Error loading ticket authors:', error)
                this.authorsError = error.message || 'Failed to load ticket authors'
            } finally {
                this.isLoadingAuthors = false
            }
        }

        selectAuthor(author: Company) {
            this.selectedAuthor = author
            this.selectedTicket = null
            this.loadTickets(author.id)
        }

        async loadTickets(authorId: string) {
            this.isLoadingTickets = true
            this.ticketsError = null
            this.tickets = []
            this.loadingProgress = 'Searching tickets...'

            try {
                const token = useCookie('token').value

                if (!token) {
                    throw new Error('Authentication token not found. Please log in.')
                }


                // Get the internal Intercom company ID for zero_star_hotels from localStorage
                let zeroStarHotelsCompanyId = localStorage.getItem('zeroStarHotelsCompanyId')

                // If not found in localStorage, try MongoDB fallback
                if (!zeroStarHotelsCompanyId) {
                    try {
                        const fallbackResponse = await fetch('/proxy/messaging/config?key=zeroStarHotelsCompanyId', {
                            headers: {Accept: 'application/json', Token: token},
                        })
                        if (fallbackResponse.ok) {
                            const fallbackData = await fallbackResponse.json()
                            if (fallbackData.value) {
                                zeroStarHotelsCompanyId = fallbackData.value
                                localStorage.setItem('zeroStarHotelsCompanyId', zeroStarHotelsCompanyId!)
                            }
                        }
                    } catch { /* ignore MongoDB fallback error */ }
                }

                // If still not found, try to fetch it directly
                if (!zeroStarHotelsCompanyId) {
                    zeroStarHotelsCompanyId = await this.ensureZeroStarHotelsCompanyId()

                    if (!zeroStarHotelsCompanyId) {
                        console.error('Failed to retrieve Zero Star Hotels company ID')
                        throw new Error(
                            'Zero Star Hotels company ID could not be retrieved. Please refresh the page to initialize the messaging system.'
                        )
                    }
                }


                // Construct the URL
                // For Customer Messages, filter for tickets with type "Customer"
                // Pass the ticket type IDs to the backend for filtering
                const ticketTypeIds = this.customerTicketTypeIds.length > 0 ? this.customerTicketTypeIds.join(',') : ''


                // Use the internal company ID instead of the external ID "zero_star_hotels"
                // We're using the simplified filtering approach where the backend will use the search payload
                // to filter tickets by contact ID AND ticket type ID
                const url = `/proxy/tickets?authorId=${authorId}&companyId=${zeroStarHotelsCompanyId}&type=customer&ticketTypeIds=${ticketTypeIds}`

                // Get tickets for the selected author using the proxy endpoint
                // We're targeting only tickets for Zero Star Hotels company created by non-company users
                const response = await fetch(url, {
                    headers: {
                        Accept: 'application/json',
                        Token: token,
                    },
                })


                // Check for 404 error specifically related to company not found
                if (response.status === 404) {
                    const errorText = await response.text()
                    console.error('404 Error response:', errorText)

                    // Check if this is a "Company not found" error
                    if (errorText.includes('Company with ID') && errorText.includes('not found')) {
                        console.error('Company not found error detected')

                        // Clear the stored company ID as it might be invalid
                        localStorage.removeItem('zeroStarHotelsCompanyId')

                        // Try to fetch the company ID again
                        const refreshedCompanyId = await this.ensureZeroStarHotelsCompanyId()

                        if (refreshedCompanyId) {
                            // Retry the request with the new company ID
                            this.loadTickets(authorId)
                            return
                        } else {
                            throw new Error(
                                'Zero Star Hotels company could not be found. The messaging system may need to be reinitialized.'
                            )
                        }
                    }

                    throw new Error(`Error ${response.status}: ${errorText}`)
                }

                // If not a 404, check if the response is ok
                if (!response.ok) {
                    throw new Error(`Error ${response.status}: ${response.statusText}`)
                }

                // Log response headers for debugging
                const headers: Record<string, string> = {}
                response.headers.forEach((value, key) => {
                    headers[key] = value
                })

                // Parse the JSON response
                const data = await response.json()

                this.tickets = data.tickets || []
                this.loadingProgress = this.tickets.length > 0
                    ? `Found ${this.tickets.length} tickets, loading messages...`
                    : ''

                if (this.tickets.length > 0) {
                    if (this.tickets.length > 1) {
                    }
                }

                // Fetch messages for all tickets to determine last message sender
                await this.fetchMessagesForAllTickets()


                if (this.tickets.length === 0) {
                    this.ticketsError =
                        'No tickets found for this author. They may have only created tickets for other companies.'
                }
            } catch (error: any) {
                console.error('Error loading tickets:', error)
                this.ticketsError = error.message || 'Failed to load tickets'
            } finally {
                this.isLoadingTickets = false
            }
        }

        selectTicket(ticket: Ticket) {
            this.selectedTicket = ticket
        }

        async fetchMessagesForAllTickets() {
            if (!this.tickets.length) return


            const token = useCookie('token').value
            if (!token) return

            // Fetch messages for each ticket in parallel
            const messagePromises = this.tickets.map(async ticket => {
                try {
                    const response = await fetch(`/proxy/tickets/${ticket.id}/messages`, {
                        headers: {
                            Accept: 'application/json',
                            Token: token,
                        },
                    })

                    if (!response.ok) {
                        console.warn(`Failed to fetch messages for ticket ${ticket.id}`)
                        return
                    }

                    const data = await response.json()
                    const messages = data.messages || []
                    const ticketAuthorId = data.ticketAuthorId

                    // Process messages similar to TicketChat component
                    const processedMessages: Message[] = messages.map((msg: any) => {
                        const isAuthorMessage = ticketAuthorId && msg.senderId === ticketAuthorId
                        return {
                            id: msg.id,
                            text: maskSupportNameInText(msg.content),
                            sender: maskSupportName(msg.senderName),
                            timestamp: msg.createdAt,
                            isFromUser: msg.senderId === authStore.person?.personId,
                            senderId: msg.senderId,
                            senderExternalId: msg.senderExternalId,
                            isTicketAuthor: isAuthorMessage,
                        }
                    })

                    // Cache messages for instant display
                    ticket.cachedMessages = processedMessages

                    // Determine last message and sender role
                    if (processedMessages.length > 0) {
                        const lastMessage = processedMessages[processedMessages.length - 1]
                        const senderRole = await this.getUserRoleFromExternalId(
                            lastMessage.senderExternalId,
                            lastMessage.senderId,
                            lastMessage.sender
                        )
                        const isTO1Role = this.isUserTO1Role(senderRole)


                        ticket.lastMessage = {
                            senderId: lastMessage.senderId,
                            senderName: lastMessage.sender,
                            isTO1Role: isTO1Role,
                            timestamp: lastMessage.timestamp,
                            senderExternalId: lastMessage.senderExternalId,
                        }
                    }
                } catch (error) {
                    console.warn(`Error fetching messages for ticket ${ticket.id}:`, error)
                }
            })

            await Promise.all(messagePromises)
        }

        // Helper method to determine user role from external ID
        async getUserRoleFromExternalId(externalId?: string, senderId?: string, senderName?: string): Promise<string> {
            // If it's the current user, use their role
            // Check both externalId and senderId to match current user
            const currentUserId = authStore.person?.personId
            if (
                currentUserId &&
                (externalId === currentUserId.toString() ||
                    senderId === currentUserId.toString() ||
                    senderId === currentUserId)
            ) {
                return authStore.person?.role || ''
            }

            // If we have an external ID, try to get the role from the API
            if (externalId) {
                // Check cache first
                if (this.userRoleCache.has(externalId)) {
                    return this.userRoleCache.get(externalId)!
                }

                try {
                    const token = useCookie('token').value
                    if (token) {
                        const response = await fetch(`/proxy/users/get-user-role?user_id=${externalId}`, {
                            headers: {
                                Accept: 'application/json',
                                Token: token,
                            },
                        })

                        if (response.ok) {
                            const data = await response.json()
                            const role = data.role || 'unknown'
                            // Cache the result
                            this.userRoleCache.set(externalId, role)
                            return role
                        }
                    }
                } catch (error) {
                    console.warn(`Failed to fetch role for external ID ${externalId}:`, error)
                }
            }

            // Fallback to heuristics based on sender ID and name
            return this.getUserRoleFromHeuristics(senderId, senderName)
        }

        // Fallback method using heuristics
        getUserRoleFromHeuristics(senderId?: string, senderName?: string): string {
            // Check sender name for role indicators (case insensitive)
            const nameToCheck = (senderName || '').toLowerCase()
            if (
                nameToCheck.includes('director') ||
                nameToCheck.includes('supervisor') ||
                nameToCheck.includes('support') ||
                nameToCheck.includes('admin') ||
                nameToCheck.includes('to1') ||
                nameToCheck.includes('manager')
            ) {
                return 'support' // Treat as TO1 role
            }

            // For admin/support users, they typically have 'admin' in their sender ID
            if (
                senderId === 'admin' ||
                (senderId && senderId.includes('admin')) ||
                (senderId && senderId.includes('support'))
            ) {
                return 'support' // Treat admin/support as TO1 role
            }

            // Check if sender ID matches known TO1 user patterns
            if (
                senderId &&
                (senderId.includes('to1') ||
                    senderId.includes('TO1') ||
                    senderId.includes('director') ||
                    senderId.includes('supervisor'))
            ) {
                return 'support' // Treat as TO1 role
            }

            // Default to non-TO1 role for unknown senders
            return 'customer'
        }

        refreshTickets() {
            // Refresh tickets for the currently selected author
            if (this.selectedAuthor) {
                this.loadTickets(this.selectedAuthor.id)
            }
        }

        // No ticket creation functionality for Customer Messages

        onTicketError(errorMessage: string) {
            // Show error notification
            this.snackbarText = errorMessage
            this.snackbarColor = 'error'
            this.showSnackbar = true
        }

        onTicketUpdated(ticket: Ticket) {
            // Update the ticket in the list
            const index = this.tickets.findIndex(t => t.id === ticket.id)
            if (index !== -1) {
                this.tickets[index] = ticket
            }
        }

        async onMessageSent(ticket: Ticket) {
            // When a message is sent, refresh the ticket data to update highlighting
            await this.fetchMessagesForAllTickets()
        }

        formatDate(timestamp: number): string {
            return format(new Date(timestamp), 'MMM d, yyyy h:mm a')
        }
    }
</script>

<style scoped>
    .gmail-layout {
        height: 100%;
        width: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        position: relative; /* Changed from absolute to relative */
    }

    .gmail-columns {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
        gap: 16px; /* Add spacing between columns */
        padding: 16px; /* Add padding around the entire layout */
        min-height: 0; /* Crucial for flex items to respect parent container boundaries */
        overflow: hidden; /* Prevent overflow */
    }

    .gmail-column {
        height: 100%;
        overflow-y: auto; /* Enable vertical scrolling */
        overflow-x: hidden; /* Prevent horizontal scrolling */
        border-radius: 8px; /* Rounded corners for columns */
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05); /* Subtle shadow for depth */
        display: flex;
        flex-direction: column;
        min-height: 0; /* Crucial for flex items to respect parent container boundaries */
    }

    .companies-column {
        flex: 0 0 20%;
        min-width: 200px;
        margin-right: 4px; /* Additional spacing */
    }

    .tickets-column {
        flex: 0 0 30%;
        min-width: 250px;
        margin-right: 4px; /* Additional spacing */
    }

    .chat-column {
        flex: 1;
        min-width: 300px; /* Ensure minimum width */
        display: flex; /* Ensure proper display */
    }

    /* Mobile responsive adjustments */
    @media (max-width: 960px) {
        .gmail-columns {
            flex-direction: column;
            gap: 12px; /* Smaller gap for mobile */
            padding: 12px; /* Smaller padding for mobile */
        }

        .gmail-column {
            width: 100%;
            height: auto;
            margin-right: 0; /* Remove horizontal margins on mobile */
            margin-bottom: 12px; /* Add vertical spacing */
        }

        .companies-column,
        .tickets-column {
            flex: 0 0 auto;
            max-height: 30vh;
        }

        .chat-column {
            flex: 1;
            margin-bottom: 0; /* Remove bottom margin from last item */
        }
    }
</style>
